class LargeProductCardsStack extends HTMLElement {
  connectedCallback() {
    this.onCardClick = this.onCardClick || this.handleCardClick.bind(this);
    this.onCardKeyDown = this.onCardKeyDown || this.handleCardKeyDown.bind(this);

    if (!this.hasCardLinkHandlers) {
      this.addEventListener('click', this.onCardClick);
      this.addEventListener('keydown', this.onCardKeyDown);
      this.hasCardLinkHandlers = true;
    }

    this.section = this.closest('.hp-large-product-cards');
    if (this.disconnectCleanupRaf) {
      cancelAnimationFrame(this.disconnectCleanupRaf);
      this.disconnectCleanupRaf = null;
    }

    this.observeViewport();

    if (this.isInitialized) return;

    this.container = this.closest('.hp-large-product-cards__container');
    this.items = Array.from(this.querySelectorAll('.large-product-card__item'));
    this.cards = this.items
      .map((item) => item.querySelector('.large-product-card'))
      .filter(Boolean);
    this.sequenceCanvas = this.container?.querySelector('[data-large-product-cards-sequence-canvas]');
    this.sequenceSource = this.container?.querySelector('[data-large-product-cards-sequence]');

    if (!this.section || !this.container || this.cards.length < 2 || !window.gsap || !window.ScrollTrigger) return;

    this.isInitialized = true;
    this.setup();
  }

  disconnectedCallback() {
    this.disconnectCleanupRaf = requestAnimationFrame(() => {
      this.disconnectCleanupRaf = null;

      if (this.isConnected) return;

      this.mm?.revert();
      this.mm = null;
      this.cleanup = null;
      this.isInitialized = false;

      this.viewportObserver?.disconnect();
      this.viewportObserver = null;
      this.isNearViewport = undefined;
      this.section?.classList.remove('is-offscreen');

      if (this.hasCardLinkHandlers) {
        this.removeEventListener('click', this.onCardClick);
        this.removeEventListener('keydown', this.onCardKeyDown);
        this.hasCardLinkHandlers = false;
      }
    });
  }

  observeViewport() {
    if (!this.section || this.viewportObserver) return;

    if (!('IntersectionObserver' in window)) {
      this.setNearViewport(true);
      return;
    }

    // Wide margin so frames finish loading before the section, and the one below it, scroll in.
    this.viewportObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => this.setNearViewport(entry.isIntersecting));
      },
      { rootMargin: '400% 0px' }
    );
    this.viewportObserver.observe(this.section);
  }

  setNearViewport(isNear) {
    this.isNearViewport = isNear;
    this.section?.classList.toggle('is-offscreen', !isNear);
    this.onNearViewportChange?.(isNear);
  }

  isCardInteractiveElement(element) {
    return Boolean(
      element?.closest(
        'a, button, form, product-form, input, textarea, select, label, summary, details, [role="button"], [data-large-product-card-no-link]'
      )
    );
  }

  navigateToCardUrl(card, event = null) {
    const url = card?.dataset.largeProductCardUrl;

    if (!url) return;

    if (event?.metaKey || event?.ctrlKey) {
      window.open(url, '_blank', 'noopener');
      return;
    }

    window.location.href = url;
  }

  handleCardClick(event) {
    if (event.defaultPrevented || event.button !== 0) return;

    const target = event.target instanceof Element ? event.target : null;
    if (!target || this.isCardInteractiveElement(target)) return;

    const card = target.closest('[data-large-product-card-url]');
    if (!card || !this.contains(card)) return;

    this.navigateToCardUrl(card, event);
  }

  handleCardKeyDown(event) {
    if (event.key !== 'Enter') return;

    const target = event.target instanceof Element ? event.target : null;
    if (!target || this.isCardInteractiveElement(target)) return;

    const card = target.closest('[data-large-product-card-url]');
    if (!card || !this.contains(card)) return;

    event.preventDefault();
    this.navigateToCardUrl(card, event);
  }

  setup() {
    if (!window.gsap || !window.ScrollTrigger) return;

    window.gsap.registerPlugin(window.ScrollTrigger);

    this.mm = window.gsap.matchMedia();
    this.mm.add('(prefers-reduced-motion: no-preference)', () => {
      let timeline = null;
      let overlapSteps = [];
      let totalStackDistance = 0;
      let lastValidStackCompensation = 0;
      let initialScrollSyncRaf = null;
      let stackOverlapDelay = 0;
      let stackDelayDistance = 0;
      let sequenceSettings = null;
      let sequenceContext = null;
      let sequenceFrameUrls = [];
      let isSequenceActive = false;
      let sequenceFrameIndex = -1;
      let sequenceRequestedFrameIndex = 0;
      const sequenceCache = new Map();
      const sequenceLoading = new Map();
      const sequenceFailed = new Set();
      const sequencePreloadConcurrency = 6;
      const sequenceAlternateDirection = true;
      const fadeStartProgress = 0.97;
      const fadedOpacity = 0.01;
      const stackedCardMaxTranslateY = 15;
      const stackedCardMaxRotateX = -8;
      const stackedCardMinScale = 0.92;
      const stackedCardScaleEndProgress = 0.8;
      const stackedCardTransformOrigin = '50% 0%';
      const fadeTargets = this.cards;

      const resetCardOffsets = () => {
        this.items.forEach((item) => {
          item.style.removeProperty('margin-top');
        });
      };
      const resetFade = () => {
        fadeTargets.forEach((target) => {
          target.style.removeProperty('opacity');
          target.style.removeProperty('transform');
          target.style.removeProperty('transform-origin');
        });
      };
      const syncFadeMetrics = () => {
        totalStackDistance = 0;
        overlapSteps = [];

        this.items.forEach((item, index) => {
          if (index === 0) return;

          const overlapStep = this.getOverlapStep(index);
          overlapSteps.push(overlapStep);
          totalStackDistance += overlapStep;
        });

        // Read CSS-driven settings once per refresh instead of on every scroll frame.
        stackOverlapDelay = this.getStackOverlapDelay();
        stackDelayDistance = this.getTotalStackDelayDistance();
        sequenceSettings = getSequenceSettings();
      };
      const getCachedTotalScrollDistance = () => {
        return totalStackDistance + stackDelayDistance;
      };
      const applyFadeProgress = (timelineProgress = 0) => {
        if (!fadeTargets.length) return;

        const traveledDistance = totalStackDistance * timelineProgress;
        let pairStartDistance = 0;

        fadeTargets.forEach((target, index) => {
          if (index >= fadeTargets.length - 1) {
            target.style.opacity = '1';
            target.style.transform = 'none';
            target.style.removeProperty('transform-origin');
            return;
          }

          const overlapStep = overlapSteps[index] || 0;
          if (overlapStep <= 0) {
            target.style.opacity = '1';
            target.style.transform = 'none';
            target.style.removeProperty('transform-origin');
            return;
          }

          const localProgress = (traveledDistance - pairStartDistance) / overlapStep;
          const clampedProgress = Math.min(Math.max(localProgress, 0), 1);

          if (clampedProgress <= fadeStartProgress) {
            target.style.opacity = '1';
          } else {
            const fadeProgress = (clampedProgress - fadeStartProgress) / (1 - fadeStartProgress);
            const opacity = 1 - (1 - fadedOpacity) * Math.min(Math.max(fadeProgress, 0), 1);
            target.style.opacity = `${opacity}`;
          }

          const translateY = stackedCardMaxTranslateY * clampedProgress;
          const rotateX = stackedCardMaxRotateX * clampedProgress;
          const scaleProgress = Math.min(clampedProgress / stackedCardScaleEndProgress, 1);
          const scale = 1 - (1 - stackedCardMinScale) * scaleProgress;
          target.style.transformOrigin = stackedCardTransformOrigin;
          target.style.transform = `translate3d(0px, ${translateY}px, 0px) rotateX(${rotateX}deg) scale(${scale})`;

          pairStartDistance += overlapStep;
        });
      };
      const getSequenceFrameUrls = () => {
        if (!this.sequenceCanvas || !this.sequenceSource) return [];

        try {
          const urls = JSON.parse(this.sequenceSource.textContent || '[]');
          return Array.isArray(urls) ? urls.filter(Boolean) : [];
        } catch (_error) {
          return [];
        }
      };
      const loadSequenceFrame = (index) => {
        const image = new Image();

        image.decoding = 'async';
        image.onload = () => {
          sequenceLoading.delete(index);
          sequenceCache.set(index, image);
          if (index === sequenceRequestedFrameIndex) {
            drawSequenceFrame(index);
          }
          loadSequenceFrames();
        };
        image.onerror = () => {
          sequenceLoading.delete(index);
          sequenceFailed.add(index);
          loadSequenceFrames();
        };
        sequenceLoading.set(index, image);
        image.src = sequenceFrameUrls[index];
      };
      const loadSequenceFrames = () => {
        if (!isSequenceActive || !sequenceFrameUrls.length) return;

        const lastIndex = sequenceFrameUrls.length - 1;

        // All frames stay loaded; nearest to the current frame go first, in both directions,
        // because the sequence alternates direction per card.
        for (let distance = 0; distance <= lastIndex; distance += 1) {
          const indexes = distance === 0
            ? [sequenceRequestedFrameIndex]
            : [sequenceRequestedFrameIndex + distance, sequenceRequestedFrameIndex - distance];

          for (const index of indexes) {
            if (sequenceLoading.size >= sequencePreloadConcurrency) return;
            if (index < 0 || index > lastIndex) continue;
            if (sequenceCache.has(index) || sequenceLoading.has(index) || sequenceFailed.has(index)) continue;

            loadSequenceFrame(index);
          }
        }
      };
      const releaseSequenceFrames = () => {
        [...sequenceLoading.values(), ...sequenceCache.values()].forEach((image) => {
          image.onload = null;
          image.onerror = null;
          image.src = '';
        });
        sequenceLoading.clear();
        sequenceCache.clear();
      };
      const setSequenceActive = (isActive) => {
        if (isActive === isSequenceActive) return;

        isSequenceActive = isActive;
        if (isActive) {
          loadSequenceFrames();
        } else {
          releaseSequenceFrames();
        }
      };
      const onSequenceNearViewport = (isNear) => {
        // Start once the section approaches; frames are kept afterwards to avoid reloading.
        if (isNear) setSequenceActive(true);
      };
      const drawSequenceFrame = (index) => {
        const image = sequenceCache.get(index);

        if (!sequenceContext || !image || !image.naturalWidth || !image.naturalHeight) return;

        if (sequenceFrameIndex === index) return;

        sequenceFrameIndex = index;
        if (this.sequenceCanvas.width !== image.naturalWidth || this.sequenceCanvas.height !== image.naturalHeight) {
          this.sequenceCanvas.width = image.naturalWidth;
          this.sequenceCanvas.height = image.naturalHeight;
        }

        sequenceContext.clearRect(0, 0, this.sequenceCanvas.width, this.sequenceCanvas.height);
        sequenceContext.drawImage(image, 0, 0, this.sequenceCanvas.width, this.sequenceCanvas.height);
      };
      const applySequenceProgress = (timelineProgress = 0) => {
        if (!sequenceFrameUrls.length) return;

        const frameIndex = Math.round(Math.min(Math.max(timelineProgress, 0), 1) * (sequenceFrameUrls.length - 1));
        if (frameIndex !== sequenceRequestedFrameIndex) {
          sequenceRequestedFrameIndex = frameIndex;
          loadSequenceFrames();
        }
        drawSequenceFrame(frameIndex);
      };
      const getSequencePercentSetting = (styles, property, fallback) => {
        const value = Number.parseFloat(styles.getPropertyValue(property));
        if (!Number.isFinite(value)) return fallback;

        return Math.min(Math.max(value / 100, 0), 1);
      };
      const getSequenceSpeedSetting = (styles, property, fallback) => {
        const value = Number.parseFloat(styles.getPropertyValue(property));
        if (!Number.isFinite(value)) return fallback;

        return Math.min(Math.max(value / 100, 0), 3);
      };
      const getSequenceSettings = () => {
        const styles = window.getComputedStyle(this.section);
        const scrollStartProgress = getSequencePercentSetting(
          styles,
          '--large-product-cards-sequence-start-progress',
          0
        );
        const scrollEndProgress = getSequencePercentSetting(
          styles,
          '--large-product-cards-sequence-end-progress',
          1
        );
        const slowStartProgress = getSequencePercentSetting(
          styles,
          '--large-product-cards-sequence-slow-start-progress',
          20 / 100
        );
        const slowEndProgress = getSequencePercentSetting(
          styles,
          '--large-product-cards-sequence-slow-end-progress',
          80 / 100
        );

        return {
          scrollStartProgress: Math.min(scrollStartProgress, scrollEndProgress),
          scrollEndProgress: Math.max(scrollStartProgress, scrollEndProgress),
          slowStartProgress: Math.min(slowStartProgress, slowEndProgress),
          slowEndProgress: Math.max(slowStartProgress, slowEndProgress),
          slowSpeed: getSequenceSpeedSetting(styles, '--large-product-cards-sequence-slow-speed', 1),
        };
      };
      const applySequenceSpeedCurve = (progress = 0, settings = getSequenceSettings()) => {
        const clampedProgress = Math.min(Math.max(progress, 0), 1);
        const slowRange = settings.slowEndProgress - settings.slowStartProgress;

        if (slowRange <= 0 || settings.slowSpeed === 1) return clampedProgress;

        const weightedDistance =
          Math.min(clampedProgress, settings.slowStartProgress) +
          Math.min(Math.max(clampedProgress - settings.slowStartProgress, 0), slowRange) * settings.slowSpeed +
          Math.max(clampedProgress - settings.slowEndProgress, 0);
        const totalWeightedDistance =
          settings.slowStartProgress +
          slowRange * settings.slowSpeed +
          (1 - settings.slowEndProgress);

        if (totalWeightedDistance <= 0) return clampedProgress;

        const curvedProgress = weightedDistance / totalWeightedDistance;

        return Math.min(Math.max(curvedProgress, 0), 1);
      };
      const getSequenceProgress = (cardProgress = 0, cardIndex = 0) => {
        const settings = sequenceSettings || getSequenceSettings();
        const sequenceRange = settings.scrollEndProgress - settings.scrollStartProgress;
        if (sequenceRange <= 0) return sequenceAlternateDirection && cardIndex % 2 === 0 ? 1 : 0;

        const progress = (cardProgress - settings.scrollStartProgress) / sequenceRange;
        const clampedProgress = Math.min(Math.max(progress, 0), 1);
        const curvedProgress = applySequenceSpeedCurve(clampedProgress, settings);

        return sequenceAlternateDirection && cardIndex % 2 === 0 ? 1 - curvedProgress : curvedProgress;
      };
      const applySequenceTimelineProgress = (timelineProgress = 0) => {
        const totalScrollDistance = getCachedTotalScrollDistance();
        if (!sequenceFrameUrls.length || totalScrollDistance <= 0) return;

        const timelinePosition = totalScrollDistance * Math.min(Math.max(timelineProgress, 0), 1);
        let itemTimelinePosition = 0;
        let activeIndex = this.items.length > 1 ? 1 : 0;
        let activeProgress = 0;

        this.items.forEach((_item, index) => {
          if (index === 0) return;

          const overlapStep = overlapSteps[index - 1] || 0;
          const itemStart = itemTimelinePosition + stackOverlapDelay;
          const itemEnd = itemStart + overlapStep;

          if (timelinePosition >= itemStart) {
            activeIndex = index;
            activeProgress = overlapStep > 0 ? (timelinePosition - itemStart) / overlapStep : 1;
            activeProgress = Math.min(Math.max(activeProgress, 0), 1);
          }

          itemTimelinePosition = itemEnd;
        });

        applySequenceProgress(getSequenceProgress(activeProgress, activeIndex));
      };
      const applyStackProgress = (timelineProgress = 0) => {
        const totalScrollDistance = getCachedTotalScrollDistance();
        const progress = Math.min(Math.max(timelineProgress, 0), 1);
        const timelinePosition = totalScrollDistance * progress;
        let itemTimelinePosition = 0;

        this.items.forEach((item, index) => {
          if (index === 0) return;

          const overlapStep = overlapSteps[index - 1] || 0;
          const itemStart = itemTimelinePosition + stackOverlapDelay;
          const itemEnd = itemStart + overlapStep;
          const itemProgress = overlapStep > 0
            ? Math.min(Math.max((timelinePosition - itemStart) / overlapStep, 0), 1)
            : Number(timelinePosition >= itemStart);
          item.style.marginTop = `${-overlapStep * itemProgress}px`;
          itemTimelinePosition = itemEnd;
        });

        applyFadeProgress(progress);
        applySequenceTimelineProgress(progress);
      };
      const getCurrentScrollProgress = () => {
        const scrollTrigger = timeline?.scrollTrigger;
        if (!scrollTrigger) return 0;

        const scrollDistance = scrollTrigger.end - scrollTrigger.start;
        if (scrollDistance <= 0) return scrollTrigger.progress || 0;

        return Math.min(Math.max((window.scrollY - scrollTrigger.start) / scrollDistance, 0), 1);
      };
      const syncInitialScrollProgress = () => {
        if (!timeline?.scrollTrigger) return;

        const progress = getCurrentScrollProgress();

        timeline.progress(progress, false);
        applyStackProgress(progress);
      };
      const setupIntroSequence = () => {
        sequenceFrameUrls = getSequenceFrameUrls();

        if (!this.sequenceCanvas || !sequenceFrameUrls.length) return;

        sequenceContext = this.sequenceCanvas.getContext('2d');
        this.onNearViewportChange = onSequenceNearViewport;
        onSequenceNearViewport(this.isNearViewport === true);
      };
      const updateSectionCompensation = () => {
        const scrollDistance = totalStackDistance;
        const stackDistance = scrollDistance;

        if (stackDistance > 0) {
          lastValidStackCompensation = stackDistance;
        }

        if (lastValidStackCompensation <= 0) return;

        this.section.style.setProperty(
          '--large-product-cards-stack-compensation',
          `${lastValidStackCompensation}px`
        );
      };
      const onRefreshInit = () => {
        resetCardOffsets();
        resetFade();
        syncFadeMetrics();
      };

      window.ScrollTrigger.addEventListener('refreshInit', onRefreshInit);

      this.cards.forEach((card, index) => {
        window.gsap.set(card, {
          zIndex: index + 1,
        });
      });

      syncFadeMetrics();
      setupIntroSequence();
      updateSectionCompensation();

      timeline = window.gsap.timeline({
        scrollTrigger: {
          trigger: this.items[0],
          start: () => `bottom bottom-=${this.getStackBottomOffset()}`,
          end: () => `+=${getCachedTotalScrollDistance()}`,
          scrub: 0.8,
          pin: this.container,
          pinSpacing: true,
          anticipatePin: 1,
          invalidateOnRefresh: true,
          onRefresh: (self) => {
            resetCardOffsets();
            resetFade();
            syncFadeMetrics();
            updateSectionCompensation();
            timeline?.progress?.(self.progress, false);
            applyStackProgress(self.progress);
          },
        },
      });

      timeline.to(
        {},
        {
          duration: () => Math.max(getCachedTotalScrollDistance(), 1),
          ease: 'none',
          onUpdate: function () {
            applyStackProgress(this.progress());
          },
        },
        0
      );

      this.cleanup = () => {
        timeline?.kill();
        timeline = null;
        window.ScrollTrigger.removeEventListener('refreshInit', onRefreshInit);
        if (initialScrollSyncRaf) {
          cancelAnimationFrame(initialScrollSyncRaf);
          initialScrollSyncRaf = null;
        }
        window.removeEventListener('load', syncInitialScrollProgress);
        this.onNearViewportChange = null;
        setSequenceActive(false);
        resetCardOffsets();
        resetFade();
        this.section.style.removeProperty('--large-product-cards-stack-compensation');
      };

      initialScrollSyncRaf = requestAnimationFrame(() => {
        window.ScrollTrigger.refresh();
        syncInitialScrollProgress();
      });
      window.addEventListener('load', syncInitialScrollProgress, { once: true });

      return () => {
        this.cleanup?.();
        this.cleanup = null;
      };
    });
  }

  getStackBottomOffset() {
    const stackBottomOffset = window
      .getComputedStyle(this)
      .getPropertyValue('--large-product-cards-stack-bottom-offset');
    const parsedOffset = parseFloat(stackBottomOffset);

    return Number.isFinite(parsedOffset) ? parsedOffset : 24;
  }

  getStackOverlapDelay() {
    const stackOverlapDelay = window
      .getComputedStyle(this.section || this)
      .getPropertyValue('--large-product-cards-stack-overlap-delay');
    const parsedDelay = parseFloat(stackOverlapDelay);

    return Number.isFinite(parsedDelay) ? parsedDelay : 0;
  }

  getOverlapStep(index) {
    const previousItem = this.items[index - 1];
    const currentItem = this.items[index];

    if (!previousItem || !currentItem) return 0;

    return this.getAbsoluteTop(currentItem) - this.getAbsoluteTop(previousItem);
  }

  getStackedOffset(index) {
    const firstItem = this.items[0];
    const currentItem = this.items[index];

    if (!firstItem || !currentItem) return 0;

    return this.getAbsoluteTop(currentItem) - this.getAbsoluteTop(firstItem);
  }

  getAbsoluteBottom(element) {
    return this.getAbsoluteTop(element) + element.offsetHeight;
  }

  getAbsoluteTop(element) {
    return element.getBoundingClientRect().top + window.scrollY;
  }

  getTotalStackDistance() {
    const lastIndex = this.items.length - 1;

    return lastIndex > 0 ? this.getStackedOffset(lastIndex) : 0;
  }

  getTotalStackDelayDistance() {
    const overlapCount = Math.max(this.items.length - 1, 0);

    return (overlapCount + 1) * this.getStackOverlapDelay();
  }

  getTotalScrollDistance() {
    return this.getTotalStackDistance() + this.getTotalStackDelayDistance();
  }
}

if (!customElements.get('large-product-cards-stack')) {
  customElements.define('large-product-cards-stack', LargeProductCardsStack);
}
